import { toast } from "react-toastify";
import { t } from "i18next";
import { historyRepository } from "../../../providers/repositories/historyRepository";
import Logger from "../../../utils/Logger";
import type { RestoreResult } from "../entities/HistoryVersion";

/// Codes métier renvoyés par `/api/history/restore` méritant un message dédié.
const ERROR_MESSAGES: Record<string, string> = {
  parentMissing: "history.error.parentMissing",
  forbidden: "history.error.forbidden",
  trainingNotResolved: "history.error.trainingNotResolved",
  invalidSnapshot: "history.error.invalidSnapshot",
  accountDisabled: "history.error.accountDisabled",
};

/// Restaure une version : réécrit l'enregistrement existant, ou le recrée s'il a été supprimé.
///
/// La restauration est elle-même tracée dans l'historique côté backend, donc annulable.
///
/// Recréer un élément supprimé ramène aussi sa descendance supprimée avec lui (les questions d'une
/// partie) : un message distinct par information, plutôt qu'une phrase à tiroirs.
export const restoreHistoryVersionUseCase = {
  async exec(versionId: string): Promise<RestoreResult | null> {
    try {
      const result = await historyRepository.restore(versionId);

      toast.success(
        result.restoredChildren > 0
          ? t("history.restoreSuccessWithChildren", {
              count: result.restoredChildren,
            })
          : t("history.restoreSuccess"),
      );

      // Une restauration partielle doit se voir : fichiers non récupérables (blob supprimé du
      // storage) et références de relation élaguées faute de cible existante.
      const incomplete = [...result.skippedFields, ...result.prunedFields];

      if (incomplete.length) {
        toast.warning(
          t("history.restoreNotRecovered", { fields: incomplete.join(", ") }),
        );
      }

      if (result.failedChildren > 0) {
        toast.warning(
          t("history.restoreChildrenFailed", { count: result.failedChildren }),
        );
      }

      return result;
    } catch (error) {
      const code = error instanceof Error ? error.message : "unknown";
      toast.error(t(ERROR_MESSAGES[code] ?? "history.restoreError"));
      Logger.error("restoreHistoryVersionUseCase", error);
    }
    return null;
  },
};
